import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ApiError } from '../../api/client'
import { importApi } from '../../api/imports'
import { mappingsApi } from '../../api/mappings'
import type { FieldDef, MappingColumn, SavedMapping } from '../../api/types'
import { BbIcon, type BbIconName } from '../../components/BbIcon'
import { BbStepper } from '../../components/BbStepper'
import { IMPORT_STEPS } from './steps'
import { ErrorState, LoadingState } from '../../components/States'
import { useAsync } from '../../hooks/useAsync'
import { useImportWizard } from '../../import/ImportWizardContext'
import { ImportSettingsModal } from './ImportSettingsModal'

/* Step 3, ported from the "Map Fields" prototype.

   The grid is driven entirely by the entity definition from
   GET /api/import/entities and the headers from POST /api/uploads; the first
   pass comes from POST /api/import/suggest-mapping. Nothing here is hardcoded
   per entity. */

const NOT_MAPPED = '__none__'

/** Field-kind summary, used when the contract supplies no `help` text. */
function describe(field: FieldDef): string {
  const parts: string[] = [field.kind.toLowerCase()]
  if (field.maxLength) parts.push(`max ${field.maxLength}`)
  if (field.referenceType) parts.push(field.referenceType.toLowerCase())
  if (field.isLineField) parts.push('line scope')
  return parts.join(' · ')
}

/* The icons are aria-hidden, so each status carries its own screen-reader text.
   An optional field left alone is drawn with a greyed x-circle: the prototype
   asks for a neutral circle and that is the only other circular glyph in the
   set. */
function statusOf(
  column: MappingColumn | undefined,
  field: FieldDef,
): { cls: string; icon: BbIconName; text: string } {
  if (column?.sourceColumn) return { cls: 'bb-map-ok', icon: 'check-circle', text: 'Mapped' }
  if (field.required) return { cls: 'bb-map-warn', icon: 'warning', text: 'Required, not mapped' }
  return { cls: 'bb-map-off', icon: 'x-circle', text: 'Optional, not imported' }
}

export function ImportMappingPage() {
  const wizard = useImportWizard()
  const navigate = useNavigate()
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [suggesting, setSuggesting] = useState(false)
  const [error, setError] = useState<ApiError | null>(null)
  const [savedId, setSavedId] = useState('')
  const [savingMapping, setSavingMapping] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const saved = useAsync<SavedMapping[]>(() => mappingsApi.list(), [])

  const { upload, entity, entityType, columns, setColumns, setUnmapped } = wizard
  const sheet = wizard.sheet
  const hasColumns = columns.length > 0

  // Step 3 is entered fresh from step 2, so ask the server for a first pass.
  useEffect(() => {
    if (!upload || !entityType || hasColumns) return
    // The suggest call is the side effect this step exists for; the flag has to
    // rise with it so the grid shows a spinner rather than an empty mapping.
    // oxlint-disable-next-line react/set-state-in-effect
    setSuggesting(true)
    setError(null)
    importApi
      .suggestMapping(upload.uploadId, entityType, sheet)
      .then((result) => {
        setColumns(result.columns)
        setUnmapped(result.unmapped)
      })
      .catch((cause: unknown) =>
        setError(cause instanceof ApiError ? cause : new ApiError(0, 'NETWORK', String(cause))),
      )
      .finally(() => setSuggesting(false))
  }, [upload, entityType, sheet, hasColumns, setColumns, setUnmapped])

  if (!upload || !entity) {
    return (
      <div className="bb bb-wizard">
        <div className="bb-empty-ref">
          <BbIcon name="folder" />
          <h3>Start with a file</h3>
          <p>
            Pick an import type and upload a file first.{' '}
            <Link to="/app/import">Back to step 1</Link>.
          </p>
        </div>
      </div>
    )
  }

  const byField = new Map(columns.map((column) => [column.targetField, column]))
  const matched = columns.filter((column) => column.sourceColumn).length
  const missingRequired = entity.fields.filter(
    (field) => field.required && !byField.get(field.name)?.sourceColumn,
  )
  const entityLabel = entity.label ?? entity.name

  const setSource = (targetField: string, sourceColumn: string | null) => {
    const next = columns.some((column) => column.targetField === targetField)
      ? columns.map((column) =>
          column.targetField === targetField
            ? // A manual choice is certain, so drop the server's confidence score.
              { ...column, sourceColumn, confidence: 0 }
            : column,
        )
      : [...columns, { targetField, sourceColumn, confidence: 0 }]
    setColumns(next)
    const used = new Set(next.map((column) => column.sourceColumn).filter(Boolean))
    setUnmapped(upload.headers.filter((header) => !used.has(header)))
    // The mapping changed, so the previous validation is stale.
    wizard.setValidation(null)
  }

  const applySaved = (id: string) => {
    setSavedId(id)
    const mapping = saved.data?.find((item) => item.id === id)
    if (!mapping) return
    /* A saved mapping was written against the field set of the day it was made,
       and the entity's set shrinks as unverified fields are removed. Keeping the
       stale columns would seed the grid and the review table with fields the
       import no longer sends, so the template is intersected with what the
       entity can carry now. What is dropped is reported rather than silent — a
       user who saved a mapping around a field needs to know it is gone. */
    const current = new Set(entity?.fields.map((field) => field.name) ?? [])
    const columns = mapping.definition.columns.filter((column) => current.has(column.targetField))
    const dropped = mapping.definition.columns.length - columns.length

    setColumns(columns)
    if (dropped > 0) {
      setNote(
        `${dropped} column${dropped === 1 ? '' : 's'} from this mapping are no longer importable and were left out.`,
      )
    }
    // The template's own options travel with it; step 4 layers the tenant's
    // current settings on top, so a stale template cannot resurrect a date
    // format the workspace has since changed.
    wizard.setOptions(mapping.definition.options ?? null)
    const used = new Set(columns.map((c) => c.sourceColumn).filter(Boolean))
    setUnmapped(upload.headers.filter((header) => !used.has(header)))
    wizard.setValidation(null)
  }

  /* Emptying the mapping is what the suggest effect waits for, so Auto-Match is
     just a re-run of the call this step already makes. */
  const autoMatch = () => {
    setNote(null)
    setSavedId('')
    setColumns([])
    wizard.setValidation(null)
  }

  /* Clearing keeps a row per field so the effect above does not immediately
     re-suggest — this is a local reset, not a request for a new guess. */
  const clearMapping = () => {
    setNote(null)
    setSavedId('')
    setColumns(
      entity.fields.map((field) => ({ targetField: field.name, sourceColumn: null, confidence: 0 })),
    )
    setUnmapped(upload.headers)
    wizard.setValidation(null)
  }

  const saveMapping = async () => {
    const name = wizard.saveMappingAs.trim()
    if (!name) return
    setSavingMapping(true)
    setNote(null)
    try {
      await mappingsApi.create(name, entityType, { entityType, columns })
      setNote(`Saved as “${name}”.`)
      saved.reload()
    } catch (cause: unknown) {
      setNote(cause instanceof ApiError ? cause.message : 'Could not save this mapping.')
    } finally {
      setSavingMapping(false)
    }
  }

  return (
    <div className="bb bb-wizard">
      <BbStepper steps={IMPORT_STEPS} current={2} />

      <h2>Map Fields</h2>
      <p className="bb-map-help">
        Match your uploaded CSV columns to QuickBooks Online {entityLabel} fields.
      </p>

      {error ? (
        <ErrorState error={error} onRetry={() => setColumns([])} />
      ) : suggesting ? (
        <LoadingState label="Matching your columns…" />
      ) : (
        <div className="bb-map">
          <div>
            <table className="bb-maptable">
              <thead>
                <tr>
                  <th className="bb-map-st">
                    <span className="sr-only">Status</span>
                  </th>
                  <th>
                    QuickBooks Field <span className="bb-req">*</span>
                  </th>
                  <th>Your File Column</th>
                </tr>
              </thead>
              <tbody>
                {entity.fields.map((field) => {
                  const column = byField.get(field.name)
                  const status = statusOf(column, field)
                  const label = field.label ?? field.name
                  return (
                    <tr key={field.name}>
                      <td className="bb-map-st">
                        <span className={status.cls}>
                          <BbIcon name={status.icon} />
                          <span className="sr-only">{status.text}</span>
                        </span>
                      </td>
                      <td>
                        <div className="bb-map-f">
                          {label}{' '}
                          {field.required ? null : <span className="bb-map-opt">(Optional)</span>}
                        </div>
                        <div className="bb-map-help">{field.help ?? describe(field)}</div>
                      </td>
                      <td>
                        <select
                          className={
                            field.required && !column?.sourceColumn
                              ? 'bb-select bb-select-warn'
                              : 'bb-select'
                          }
                          id={`map-${field.name}`}
                          aria-label={`File column for ${label}`}
                          value={column?.sourceColumn ?? NOT_MAPPED}
                          onChange={(event) =>
                            setSource(
                              field.name,
                              event.target.value === NOT_MAPPED ? null : event.target.value,
                            )
                          }
                        >
                          <option value={NOT_MAPPED}>
                            {field.required ? 'Select Column…' : 'Ignore (Do not import)'}
                          </option>
                          {upload.headers.map((header) => (
                            <option value={header} key={header}>
                              {header}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <aside>
            <div className="bb-field">
              <label className="bb-aside-h" htmlFor="savedMap">
                Choose saved Mappings
              </label>
              <select
                className="bb-select"
                id="savedMap"
                value={savedId}
                onChange={(event) => applySaved(event.target.value)}
              >
                <option value="">Please select</option>
                {(saved.data ?? [])
                  .filter((mapping) => mapping.entityType === entityType)
                  .map((mapping) => (
                    <option value={mapping.id} key={mapping.id}>
                      {mapping.name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="bb-field">
              <label className="bb-flabel" htmlFor="saveMappingAs">
                Name for this mapping
              </label>
              <input
                className="bb-input"
                id="saveMappingAs"
                placeholder="e.g. Monthly invoices"
                value={wizard.saveMappingAs}
                onChange={(event) => wizard.setSaveMappingAs(event.target.value)}
              />
            </div>

            <div className="bb-tilebtns">
              <button
                className="bb-tilebtn"
                type="button"
                disabled={savingMapping || !wizard.saveMappingAs.trim()}
                onClick={() => void saveMapping()}
              >
                <span className="bb-tilebtn-i">
                  <BbIcon name="save" />
                </span>
                Save Mappings
              </button>
              <button className="bb-tilebtn" type="button" onClick={autoMatch}>
                <span className="bb-tilebtn-i">
                  <BbIcon name="sync" />
                </span>
                Auto-Match
              </button>
              <button className="bb-tilebtn" type="button" onClick={clearMapping}>
                <span className="bb-tilebtn-i">
                  <BbIcon name="layers-clear" />
                </span>
                Clear Mapping
              </button>
            </div>

            <p className="bb-map-help" role="status">
              {note ??
                (missingRequired.length
                  ? `${matched} of ${entity.fields.length} fields mapped. Still missing: ${missingRequired
                      .map((field) => field.label ?? field.name)
                      .join(', ')}.`
                  : `${matched} of ${entity.fields.length} fields mapped.`)}
            </p>
          </aside>
        </div>
      )}

      <div className="bb-wizard-foot">
        <Link className="bb-btn bb-btn-quiet" to="/app/import/upload">
          <BbIcon name="arrow-left" />
          Previous Step
        </Link>
        <span className="bb-wizard-foot-r">
          <button className="bb-btn bb-btn-quiet" type="button" onClick={() => setSettingsOpen(true)}>
            <BbIcon name="tune" />
            Settings
          </button>
          <button
            className="bb-btn bb-btn-green"
            type="button"
            disabled={missingRequired.length > 0}
            onClick={() => navigate('/app/import/review')}
          >
            Next Step
            <BbIcon name="arrow-right" />
          </button>
        </span>
      </div>

      <ImportSettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  )
}
